import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useCloud } from '../context/CloudContext';
import { useAuth } from '../context/AuthContext';
import { Loading, ErrorState, EmptyState } from '../components/States';
import { recordVisit } from '../lib/recent';

const CATEGORY_ORDER = [
  'Compute',
  'Storage',
  'Database',
  'Networking',
  'Security',
  'Analytics',
];

export default function AllServices() {
  const navigate = useNavigate();
  const { activeProvider, providers: ctxProviders } = useCloud();
  const { isAdmin } = useAuth();
  const { data, loading, error, reload } = useAsync(async () => {
    const providers = await api.getProviders();
    const lists = await Promise.all(providers.map((p) => api.getServices(p.id)));
    const byId = Object.fromEntries(providers.map((p) => [p.id, p]));
    const services = lists.flat().map((s) => ({
      ...s,
      providerShort: byId[s.providerId]?.shortName || s.providerId,
    }));
    return services;
  }, []);

  if (loading) return <Loading label="Loading services…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  // Only the selected provider's services are shown.
  const services = activeProvider
    ? data.filter((s) => s.providerId === activeProvider)
    : data;
  const activeName = ctxProviders.find((p) => p.id === activeProvider)?.name || '';
  const categories = {};
  for (const s of services) {
    (categories[s.category] ||= []).push(s);
  }
  const orderedCats = [
    ...CATEGORY_ORDER.filter((c) => categories[c]),
    ...Object.keys(categories).filter((c) => !CATEGORY_ORDER.includes(c)),
  ];

  const openService = (s) => {
    recordVisit('services', {
      id: `${s.providerId}:${s.id}`,
      name: s.name,
      category: s.category,
      providerId: s.providerId,
      providerShort: s.providerShort,
    });
    let dest;
    if (s.id === 'vault') dest = '/vaults';
    else if (s.category === 'Security') dest = '/iam';
    else dest = `/services/${s.providerId}/${s.id}`;
    navigate(dest);
  };

  return (
    <div className="services-layout">
      <section className="services-panel">
        <h1 className="services-panel__title">
          Services by category{activeName ? ` · ${activeName}` : ''}
        </h1>
        {orderedCats.length === 0 ? (
          // Non-admins only see services granted by a policy on one of their
          // groups, so an empty catalogue means "nothing granted", not "nothing
          // exists".
          <EmptyState
            title="No services available"
            detail={
              isAdmin
                ? 'The provider catalogue is empty.'
                : 'Your user group has no policy granting access to a service yet. Ask an administrator to add one.'
            }
          />
        ) : null}
        <div className="services-cats">
          {orderedCats.map((cat) => (
            <div className="svc-cat" key={cat}>
              <div className="svc-cat__head">
                <span className="svc-cat__name">{cat}</span>
              </div>
              <ul className="svc-cat__list">
                {categories[cat].map((s) => (
                  <li key={`${s.providerId}:${s.id}`}>
                    <button className="svc-link" onClick={() => openService(s)}>
                      {s.name}
                      <span className="svc-link__provider">{s.providerShort}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
